import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Package, Menu, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loginDropdownOpen, setLoginDropdownOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Tracking', path: '/tracking' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' }
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="bg-gradient-to-br from-slate-800 to-slate-600 p-2.5 rounded-lg group-hover:shadow-lg transition-all duration-300">
              <Package className="h-7 w-7 text-white" />
            </div>
            <div>
              <span className="text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
                SABEY
              </span>
              <span className="text-sm block text-slate-600 font-medium -mt-1">LOGISTICS</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  location.pathname === link.path
                    ? 'text-slate-800 bg-slate-100'
                    : 'text-slate-600 hover:text-slate-800 hover:bg-slate-50'
                }`}
              >
                {link.name}
              </Link>
            ))}
            
            {/* Login Dropdown */}
            <div className="relative">
              <button
                onClick={() => setLoginDropdownOpen(!loginDropdownOpen)}
                onBlur={() => setTimeout(() => setLoginDropdownOpen(false), 200)}
                data-testid="header-login-dropdown-btn"
                className="px-4 py-2 rounded-lg font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-50 transition-all duration-200 flex items-center space-x-1"
              >
                <span>Login</span>
                <ChevronDown className="h-4 w-4" />
              </button>
              
              {loginDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-2 z-50">
                  <Link
                    to="/customer/login"
                    className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                    onClick={() => setLoginDropdownOpen(false)}
                    data-testid="header-customer-login-link"
                  >
                    Customer Login
                  </Link>
                  <div className="border-t border-slate-100 my-1"></div>
                  <Link
                    to="/driver/login"
                    className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                    onClick={() => setLoginDropdownOpen(false)}
                  >
                    Driver Login
                  </Link>
                  <Link
                    to="/dispatcher/login"
                    className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                    onClick={() => setLoginDropdownOpen(false)}
                  >
                    Dispatcher Login
                  </Link>
                  <Link
                    to="/admin/login"
                    className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                    onClick={() => setLoginDropdownOpen(false)}
                    data-testid="header-admin-login-link"
                  >
                    Admin Login
                  </Link>
                </div>
              )}
            </div>
          </nav>

          {/* CTA Button */}
          <div className="hidden md:block">
            <Link to="/contact">
              <Button className="bg-gradient-to-r from-slate-800 to-slate-600 hover:from-slate-700 hover:to-slate-500 text-white px-6 py-2.5 rounded-lg font-semibold shadow-md hover:shadow-lg transition-all duration-300">
                Get a Quote
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white">
          <div className="px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-3 rounded-lg font-medium transition-colors ${
                  location.pathname === link.path
                    ? 'text-slate-800 bg-slate-100'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                {link.name}
              </Link>
            ))}
            
            {/* Mobile Login Links */}
            <div className="pt-2 border-t border-slate-200">
              <div className="text-xs font-semibold text-slate-500 px-4 py-2">LOGIN PORTALS</div>
              <Link
                to="/customer/login"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 rounded-lg font-medium text-slate-600 hover:bg-slate-50"
              >
                Customer Login
              </Link>
              <Link
                to="/driver/login"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 rounded-lg font-medium text-slate-600 hover:bg-slate-50"
              >
                Driver Login
              </Link>
              <Link
                to="/dispatcher/login"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 rounded-lg font-medium text-slate-600 hover:bg-slate-50"
              >
                Dispatcher Login
              </Link>
              <Link
                to="/admin/login"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 rounded-lg font-medium text-slate-600 hover:bg-slate-50"
              >
                Admin Login
              </Link>
            </div>
            
            <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
              <Button className="w-full bg-gradient-to-r from-slate-800 to-slate-600 text-white mt-2">
                Get a Quote
              </Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;